import { useMemo } from 'react';
import { confirmedRequirementIds } from '../declarations';
import { CERTREQ_URI_PREFIX } from '../requirementKeys';
import { ValidationResult } from '../types';
import { useCurrentApplication } from '../../hooks';
import { buildUserView, UserView } from './userRequirements';
import { useUserViewTexts } from './useUserViewTexts';

/**
 * Filnavnene som ligger på hvert krav, slått opp på kravets `requirementId`.
 *
 * Vedleggene bærer nøkkelen `certreq:{kode}#{requirementId}` i
 * `simulatorResultItemUri`. Vi leser den bakfra i stedet for å gå gjennom
 * kravtreet: modellen trenger kartet FØR den er bygget, og nøkkelformatet er
 * det samme uansett hvilket krav det gjelder.
 */
type UploadedAttachment = {
  simulatorResultItemUri?: string | null;
  blobName?: string | null;
};

const uploadedFilesByRequirement = (
  attachments: UploadedAttachment[] | null | undefined,
  certificateCode: string
): Map<string, string[]> => {
  const prefix = `${CERTREQ_URI_PREFIX}:${certificateCode}#`;
  const files = new Map<string, string[]>();

  (attachments || []).forEach((attachment) => {
    const uri = attachment.simulatorResultItemUri || '';
    if (!uri.startsWith(prefix)) return;
    const requirementId = uri.slice(prefix.length);
    if (!requirementId) return;
    const existing = files.get(requirementId);
    if (existing) existing.push(attachment.blobName || '');
    else files.set(requirementId, [attachment.blobName || '']);
  });

  return files;
};

/**
 * Søkervisningen av kravtreet — ferdig bygget, med søknadens vedlegg og
 * erklæringer lagt oppå.
 *
 * Hooken finnes fordi TO steder trenger den samme modellen: selve visningen
 * tegner den, og kravsiden leser `status` av den for å vite om noe fortsatt
 * mangler dokumentasjon (det er den som avgjør «Neste»-sperren og om
 * overprøvingen skal tilbys). Uten en delt hook måtte oppskriften — vedlegg,
 * erklæringer, språk og reservenavn — stått to steder, og de to kunne komme i
 * utakt.
 *
 * Uten kravtre finnes ingen visning, og hooken svarer `null`.
 */
export const useUserView = (
  result: ValidationResult | null | undefined,
  certificateName: string,
  certificateCode: string
): UserView | null => {
  const { texts, translate, requirementTypeLabel } = useUserViewTexts();
  const { application } = useCurrentApplication();

  const attachments = application.result?.attachments;
  const confirmations = application.result?.confirmedQualifications;

  const uploadedFiles = useMemo(
    () => uploadedFilesByRequirement(attachments, certificateCode),
    [attachments, certificateCode]
  );

  /*
   * Erklæringene søkeren har avgitt. De ligger på søknaden, ikke i motorens
   * svar — motoren vurderer ikke kravtypen i det hele tatt.
   */
  const confirmedRequirements = useMemo(
    () => confirmedRequirementIds(confirmations, certificateCode),
    [confirmations, certificateCode]
  );

  return useMemo(
    () =>
      result
        ? buildUserView(result, {
            certificateName,
            texts,
            translate,
            uploadedFiles,
            confirmedRequirements,
            // Mangler kravet `label`, brukes kravtypen på norsk — vi dikter ikke
            // opp en forklaring dataene ikke har. Ukjente kravtyper gir tom
            // streng, og da faller navnet videre til kravets id.
            titleFallback: (node) =>
              node.requirementType ? requirementTypeLabel(node.requirementType) : '',
            // Samme katalog brukes til å kjenne igjen begrunnelser som bare
            // gjentar kravtypen («Mangler sertifikat GOC» ved kravet «GOC»).
            requirementTypeLabel,
          })
        : null,
    [
      result,
      certificateName,
      texts,
      translate,
      requirementTypeLabel,
      uploadedFiles,
      confirmedRequirements,
    ]
  );
};

export default useUserView;
